import { useEffect } from 'react';
import { filter, Subscription } from 'rxjs';
import { State } from '@/services/state.service';
import { SipJs } from '@/services/sip-js.service';
import { IWrapUp } from '@/shared/interfaces/wrapUp.interface';
import { LOCAL_STORAGE } from '@/shared/constants/localStorage';
import { EPresenceStatusApi } from '@/shared/enums/presence.enum';
import { CALL_PAGE_TYPE, CALL_PAGE_STATUS } from '@/shared/constants/callPage';
import { useStore } from '@/stores';

interface UseWrapUpTimerReturn {
  wrapUp: IWrapUp;
  removeWrapUp: () => void;
}

const MIN_TIME_THRESHOLD = 2;

export const useWrapUpTimer = (): UseWrapUpTimerReturn => {
  const { callStore } = useStore();

  const onChangePresence = (
    presenceStatus: EPresenceStatusApi,
    storePresence: boolean = false,
  ) => {
    if (storePresence) {
      localStorage.setItem(LOCAL_STORAGE.LS_MANUAL_PRESENCE, presenceStatus);
    }
    SipJs.pushNotification(presenceStatus, true);
  };

  const removeWrapUp = (): void => {
    callStore.clearWrapUpTimer();
    State.setState({ wrapUpQueue: null });
    onChangePresence(EPresenceStatusApi.AVAILABLE, true);
    callStore.setCallPage({
      page: CALL_PAGE_TYPE.HIDDEN,
      status: CALL_PAGE_STATUS.INACTIVE,
    });
    callStore.setWrapUpSession(null);
    callStore.clearCallNote();
    callStore.setActiveContact(null);
  };

  const startCountdown = (initialTime: number): void => {
    if (callStore.wrapUpTimer) {
      return;
    }

    callStore.setWrapUpCounter(initialTime);

    const timer = setInterval(() => {
      const currentTime = callStore.wrapUpCounter;

      if (currentTime === null) return;

      if (currentTime < MIN_TIME_THRESHOLD) {
        removeWrapUp();
        return;
      }

      callStore.setWrapUpCounter(currentTime - 1);
    }, 1000);

    callStore.setWrapUpTimer(timer);
  };

  useEffect(() => {
    const subscription: Subscription = State.state$
      .pipe(filter((state) => Boolean(state.wrapUpQueue?.isWorking)))
      .subscribe((state) => {
        const { time } = state.wrapUpQueue!;

        if (callStore.wrapUpTimer) {
          return;
        }

        onChangePresence(EPresenceStatusApi.WRAP_UP);
        startCountdown(time);
      });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const wrapUp: IWrapUp = {
    show: callStore.wrapUpCounter !== null && callStore.wrapUpCounter > 0,
    time: callStore.wrapUpCounter ?? 0,
  };

  return {
    wrapUp,
    removeWrapUp,
  };
};
